import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Map, Palette, MonitorPlay, Minimize2, List, CalendarDays, Box, X, Ruler, Wand2, Eye, Images } from "lucide-react";
import { toast } from "sonner";
import { useUnits, fmtDims } from "@/lib/units";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, asset, money, applyTheme, track, beacon } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandMark } from "@/components/Brand";
import { LeadDialog } from "@/components/LeadForms";
import { useMatterport, jumpTo } from "@/components/viewer/useMatterport";
import { HotspotLayer } from "@/components/viewer/HotspotLayer";
import { RoomList, HotspotDetail } from "@/components/viewer/Panels";
import { TourBar } from "@/components/viewer/TourBar";
import { PersonaliseDrawer, SaveHomeDialog, calcTotal } from "@/components/viewer/Personalise";
import { ChatWidget } from "@/components/viewer/ChatWidget";
import { CtaChooser } from "@/components/CtaChooser";
import { useAmbient, useGlide, GlideCaption, ExtraButtons, ExtraDialog, IntroVideo, ClosingCard, homeFacts, AgentBadge, TryTitle, TryTip } from "@/components/viewer/TourExtras";
import { EditorPanel } from "@/components/viewer/EditorPanel";
import { FloorPlanViewer } from "@/components/viewer/FloorPlanPins";
import { GalleryOverlay } from "@/components/viewer/Gallery";
import { TourMenus, CompactActions } from "@/components/viewer/TourMenus";
import { sellsHomes, setViewerTerms, vt, assetLine, ctaLinks, openCta, iguideEmbed } from "@/lib/industry";
import { PumpjackRecords } from "@/components/viewer/PumpjackRecords";
import { ClipboardList, Volume2, VolumeX } from "lucide-react";
import { RoomSwiper } from "@/components/viewer/RoomSwiper";
import { PasswordGate, StartScreen, Watermark } from "@/components/viewer/TourGate";
import { resolveSkin, skinRootProps, barClass, byOrder, sectionRank } from "@/lib/skin";

const IconBtn = ({ children, big, active, className = "", ...p }) => (
  <button {...p} className={`${className} glass-dark rounded-full inline-flex items-center justify-center gap-2 font-semibold transition-[background-color,transform] active:scale-95 ${big ? "h-14 px-5 text-base min-w-[56px]" : "h-11 px-4 text-sm min-w-[44px]"} ${active ? "!bg-white !text-slate-900" : ""}`}>{children}</button>
);

function LoadingVeil({ show, house, builder }) {
  return (
    <div className={`absolute inset-0 z-[6] bg-slate-950 transition-opacity duration-700 ${show ? "opacity-100" : "opacity-0 pointer-events-none"}`} aria-hidden={!show} data-testid="viewer-loading-veil">
      {house.photos?.[0] && <img src={asset(house.photos[0])} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 blur-md scale-105" />}
      <div className="absolute inset-0 grid place-items-center text-center px-6">
        <div>
          {builder.logo_url ? <img src={asset(builder.logo_url)} alt="" className="h-12 mx-auto mb-4 object-contain" /> : <Box className="w-10 h-10 mx-auto mb-3 text-ba animate-pulse" />}
          <p className="font-serif text-xl">{house.name}</p>
          <p className="text-sm text-white/70 mt-2 inline-flex items-center gap-2" data-testid="viewer-status"><span className="w-2 h-2 rounded-full bg-ba animate-pulse" />Loading 3D tour…</p>
        </div>
      </div>
    </div>
  );
}

function Fallback({ status, house }) {
  if (status === "ready" || status === "loading") return null;
  return (
    <div className="absolute inset-0 z-0" data-testid="viewer-fallback">
      <img src={asset(house.photos?.[0])} alt="" className="w-full h-full object-cover opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/70" />
      <div className="absolute inset-0 grid place-items-center text-center px-6 text-white">
        <div>
          <Box className={`w-10 h-10 mx-auto mb-3 text-ba ${status === "loading" ? "animate-pulse" : ""}`} />
          <p className="font-serif text-2xl" data-testid="viewer-status">{status === "unavailable" ? "3D tour coming soon" : "3D tour unavailable here"}</p>
          {status === "blocked" && <p className="text-sm text-white/70 mt-2 max-w-sm">The Matterport viewer only runs on the published, whitelisted domain. Rooms, finishes and pricing are still available.</p>}
        </div>
      </div>
    </div>
  );
}

function IguidePlans({ url }) {
  return (
    <div className="relative w-full max-w-6xl">
      <iframe src={iguideEmbed(url)} title="Measured floor plans" className="w-full h-[calc(100dvh-7rem)] rounded-2xl border-0 bg-white" allowFullScreen data-testid="iguide-iframe" />
      <a href={iguideEmbed(url).replace("/embed/", "/")} target="_blank" rel="noreferrer" className="absolute bottom-3 right-3 h-9 px-4 rounded-full glass-dark text-white text-xs font-semibold inline-flex items-center" data-testid="iguide-open-new">Open in new tab</a>
    </div>
  );
}

export default function Viewer({ embed = false, tryMode = false }) {
  const { id } = useParams();
  const [qs, setQs] = useSearchParams();
  const plotId = qs.get("plot") || "";
  const presenter = qs.get("present") === "1";
  const { user } = useAuth();
  const [bundle, setBundle] = useState(null);
  const isSuper = user?.platform_role === "superadmin";
  const canEdit = !tryMode && !!user && !!bundle && (isSuper ? !!(bundle.builder.is_demo || bundle.builder.slug === "hearthstone") : user.builder_id === bundle.builder.id && (user.perms || []).some((p) => p === "content" || p === "design"));
  const edit = qs.get("edit") === "1" && canEdit;
  const setEdit = (on) => { const n = new URLSearchParams(qs); if (on) n.set("edit", "1"); else n.delete("edit"); setQs(n); };
  const [cfg, setCfg] = useState(null);
  const [choices, setChoices] = useState({});
  const [open, setOpen] = useState(null);
  const [personalise, setPersonalise] = useState(false);
  const [focusCat, setFocusCat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lead, setLead] = useState(null);
  const [roomsSheet, setRoomsSheet] = useState(false);
  const [records, setRecords] = useState(false);
  const [floor, setFloor] = useState(false);
  const [planTab, setPlanTab] = useState("plan");
  const [gallery, setGallery] = useState(false);
  const [playSignal, setPlaySignal] = useState(0);
  const [locked, setLocked] = useState(null);
  const [intro, setIntro] = useState(true);
  const started = useRef(Date.now());
  const [unit, toggleUnit] = useUnits();
  const skinPreview = qs.get("skin_preview") === "1";
  const [pv, setPv] = useState(null);
  useEffect(() => {
    if (!skinPreview) return undefined;
    const on = (e) => { if (e.origin === window.location.origin && e.data?.type === "sh-skin") setPv(e.data.skin); };
    window.addEventListener("message", on);
    window.parent?.postMessage({ type: "sh-skin-ready" }, window.location.origin);
    return () => window.removeEventListener("message", on);
  }, [skinPreview]);
  useEffect(() => {
    if (bundle) applyTheme(pv || resolveSkin(bundle.builder, bundle.house_type));
  }, [bundle, pv]);
  const spaceIds = (qs.get("spaces") || "").split(",").filter(Boolean);
  const [spaces, setSpaces] = useState([]);
  useEffect(() => {
    if (!bundle || spaceIds.length < 2) return;
    api.get(`/public/builders/${bundle.builder.slug}`).then((r) => setSpaces(spaceIds.map((s) => r.data.house_types.find((h) => h.id === s)).filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle?.builder.id, qs.get("spaces")]);

  const load = useCallback(() => api.get(tryMode ? `/public/try/${id}` : `/public/house-types/${id}`, { params: plotId ? { plot_id: plotId } : {}, headers: { "X-Tour-Key": sessionStorage.getItem(`tourkey_${id}`) || "" } }).then((r) => {
    setLocked(null);
    setBundle(r.data);
    applyTheme(r.data.builder);
    return r.data;
  }).catch((e) => {
    if (e.response?.status === 423) { setLocked(e.response.data.detail); applyTheme(e.response.data.detail.builder); }
    throw e;
  }), [id, plotId, tryMode]);

  const boot = useCallback(() => load().then(async (b) => {
      const def = Object.fromEntries(b.finishes.map((c) => [c.id, (c.options.find((o) => !o.is_upgrade) || c.options[0])?.id]));
      const sel = qs.get("sel");
      if (sel) {
        const { data } = await api.get(`/public/selections/${sel}`).catch(() => ({ data: null }));
        data?.selection.items.forEach((i) => { def[i.category_id] = i.option_id; });
      }
      setChoices(def);
    }).catch(() => {}), [load, qs]);

  useEffect(() => {
    api.get("/public/config").then((r) => setCfg(r.data));
    boot();
    if (qs.get("skin_preview") !== "1") track({ type: "view", house_type_id: id });
    started.current = Date.now();
    const leave = () => beacon({ type: "tour_time", house_type_id: id, duration: (Date.now() - started.current) / 1000 });
    window.addEventListener("pagehide", leave);
    return () => { window.removeEventListener("pagehide", leave); leave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (edit && user?.platform_role === "superadmin" && bundle) localStorage.setItem("ih_builder", bundle.builder.id);
  }, [edit, user, bundle]);

  const mp = useMatterport(bundle?.house_type.matterport_model_id, cfg?.matterport_sdk_key, edit);
  const [health, setHealth] = useState({});
  const [booked, setBooked] = useState({});
  useEffect(() => {
    const hid = bundle?.house_type?.id;
    if (bundle?.builder?.industry !== "fitness" || !hid) return;
    api.get(`/public/house-types/${hid}/booked`).then((r) => setBooked(r.data)).catch(() => setBooked({}));
  }, [bundle]);
  const hotspots = useMemo(() => {
    const list = [...(bundle?.hotspots || [])].sort(byOrder);
    if (bundle?.builder?.industry !== "energy") return list;
    return list.map((h) => ({ ...h, kind: h.kind === "room" ? "part" : h.kind, health: health[h.id]?.status, health_date: health[h.id]?.date }));
  }, [bundle, health]);
  useEffect(() => {
    const hid = bundle?.house_type?.id;
    if (bundle?.builder?.industry !== "energy" || !bundle?.house_type?.has_pumpjack) return;
    api.get(`/public/house-types/${hid}/part-health`, { headers: { "X-Tour-Key": sessionStorage.getItem(`tourkey_${hid}`) || "" } }).then((r) => setHealth(r.data)).catch(() => setHealth({}));
  }, [bundle]);
  const selectRef = useRef(null);
  const sharedDone = useRef(false);
  const roomParam = qs.get("room");
  useEffect(() => {
    if (!bundle || !roomParam || sharedDone.current || mp.status === "loading") return;
    const h = bundle.hotspots.find((x) => x.id === roomParam);
    if (!h) return;
    sharedDone.current = true;
    setIntro(false);
    selectRef.current?.(h);
  }, [bundle, roomParam, mp.status]);
  const startDone = useRef(false);
  const [veil, setVeil] = useState(true);
  const [extra, setExtra] = useState(null);
  const ht = bundle?.house_type;
  const glideStops = useMemo(() => hotspots.filter((x) => (x.kind === "room" || x.kind === "area") && (x.pose || x.position)).slice(0, 5), [hotspots]);
  const glideSteps = useMemo(() => {
    const s = ht?.intro_steps || [];
    const homeSite = sellsHomes(bundle?.builder);
    if (s.length) return s.map((x) => { const h = hotspots.find((y) => y.id === x.hotspot_id && (y.pose || y.position)); return { h, auto: x.auto, hold: x.hold, tip: x.tip, caption: x.caption, transition: x.transition, facts: homeSite ? homeFacts(x.facts, ht, h, unit) : [] }; }).filter((x) => x.h || x.caption);
    return glideStops.map((h, i) => ({ h, caption: (ht?.intro_captions || [])[i] || h.title, transition: "fly" }));
  }, [ht, hotspots, glideStops, bundle, unit]);
  const startTargetRef = useRef(null);
  const [videoDone, setVideoDone] = useState(false);
  const tourOn = !intro || ht?.start_screen === false;
  const needVideo = !!ht?.intro_video && !edit && !roomParam && !videoDone;
  const [muted, toggleMute] = useAmbient(edit ? "" : ht?.ambient_audio || (tryMode ? "/seed/ambient.wav" : ""), tourOn && !needVideo);
  const [titleShow, setTitleShow] = useState(false);
  const titleDone = useRef(false);
  useEffect(() => {
    if (!tryMode || !tourOn || titleDone.current) return undefined;
    titleDone.current = true;
    setTitleShow(true);
    const t = setTimeout(() => setTitleShow(false), 4500);
    return () => clearTimeout(t);
  }, [tryMode, tourOn]);
  const [glideCap, cine, closing] = useGlide(mp.sdk, mp.sweepsRef, glideSteps, !!ht?.intro_glide && !edit && !roomParam && tourOn && !veil && !needVideo && !titleShow, startTargetRef, ht?.intro_return || "fly", sellsHomes(bundle?.builder) && ht?.kind !== "scan" && ht?.intro_closing !== false ? 5000 : 0, mp.poseRef);
  const hideCls = cine ? "opacity-0 pointer-events-none" : "opacity-100";
  const reveal = () => setTimeout(() => setVeil(false), 700);
  useEffect(() => {
    if (!mp.src) return undefined;
    if (mp.status === "blocked") { setVeil(false); return undefined; }
    if (mp.status !== "ready") return undefined;
    const t = setTimeout(() => setVeil(false), 12000);
    return () => clearTimeout(t);
  }, [mp.status, mp.src]);
  useEffect(() => {
    if (!bundle || startDone.current || mp.status !== "ready" || !mp.sdk) return;
    if (edit || roomParam) { startDone.current = true; reveal(); return; }
    startDone.current = true;
    const pref = bundle.house_type.start_hotspot_id;
    const sp = bundle.house_type.start_pose;
    const placed = hotspots.filter((x) => x.pose || x.position);
    const rooms = placed.filter((x) => x.kind === "room" || x.kind === "part" || x.kind === "area");
    const h = placed.find((x) => x.id === pref) || rooms[0];
    const target = pref === "matterport" ? null : pref === "view" && sp ? sp : h ? { ...(h.pose || {}), position: h.pose?.position || h.position } : null;
    startTargetRef.current = target;
    const sdk = mp.sdk;
    (async () => {
      try { await sdk.App.state.waitUntil((s) => s.phase === sdk.App.Phase.PLAYING); } catch { /* older SDK */ }
      if (target) {
        for (let i = 0; i < 6; i += 1) {
          if (await jumpTo(sdk, target, "instant", "normal", mp.sweepsRef.current)) break;
          await new Promise((r) => { setTimeout(r, 1000); });
        }
      }
      reveal();
    })();
  }, [bundle, hotspots, edit, roomParam, mp.status, mp.sdk, mp.sweepsRef]);
  if (locked && !bundle) return <PasswordGate hid={id} info={locked} onUnlocked={boot} />;
  if (!bundle) return <div className="h-[100dvh] bg-slate-950" />;
  const { house_type: house, plot, builder, development } = bundle;
  const sells = sellsHomes(builder);
  const links = ctaLinks(builder, house);
  setViewerTerms(builder, links);
  const goLink = (l, hs) => openCta(l, { builder_id: builder.id, house_type_id: house.id, ...(hs?.id ? { hotspot_id: hs.id } : {}) });
  const book = () => (tryMode ? toast("In your own tour, this opens your booking calendar or link. Book a demo to set it up.") : links[0] ? goLink(links[0]) : setLead("viewing"));
  const bookFrom = (hs) => (links[0] ? goLink(links[0], hs) : setLead("viewing"));
  const finishes = sells ? bundle.finishes : [];
  const base = plot?.price || house.base_price;
  const total = calcTotal(base, finishes, choices).total;
  const ctx = { house_type_id: house.id, plot_id: plot?.id, development_id: development?.id, label: `${house.name}${plot ? ` · Lot ${plot.plot_number}` : ""}` };

  const select = (h, fromPin = false) => {
    setOpen(h);
    setRoomsSheet(false);
    track({ type: "hotspot_click", house_type_id: house.id, hotspot_id: h.id, label: h.title });
    const move = bundle?.builder?.skin?.hotspot_transition || "fly";
    const speed = bundle?.builder?.skin?.move_speed || "normal";
    if ((h.pose || h.position) && mp.sdk && !(fromPin && h.info_only)) { setFloor(false); jumpTo(mp.sdk, { ...(h.pose || {}), position: h.pose?.position || h.position }, move, speed, mp.sweepsRef.current); }
  };
  selectRef.current = select;
  const share = async (h) => {
    const url = `${window.location.origin}/view/${house.id}?${new URLSearchParams({ ...(plot ? { plot: plot.id } : {}), room: h.id })}`;
    const text = `Take a look at the ${h.title} in the ${house.name}`;
    if (navigator.share) { try { await navigator.share({ title: h.title, text, url }); return; } catch (e) { if (e?.name === "AbortError") return; } }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied. Anyone who opens it lands right here."); } catch { window.prompt("Copy this link", url); }
  };
  const pick = (c, o) => {
    setChoices({ ...choices, [c.id]: o.id });
    if (o.is_upgrade) track({ type: "upgrade_pick", house_type_id: house.id, option_id: o.id, label: `${c.name}: ${o.name}` });
  };
  const toggleFloor = async () => {
    const next = !floor;
    setFloor(next);
    if (mp.sdk && edit) await mp.sdk.Mode.moveTo(next ? mp.sdk.Mode.Mode.FLOORPLAN : mp.sdk.Mode.Mode.INSIDE).catch(() => {});
  };
  const togglePresenter = () => {
    const n = new URLSearchParams(qs);
    if (presenter) { n.delete("present"); document.fullscreenElement && document.exitFullscreen?.(); }
    else { n.set("present", "1"); document.documentElement.requestFullscreen?.().catch(() => {}); }
    setQs(n, { replace: true });
  };
  const openPersonalise = (cat) => { if (builder.design_studio_mode) { setLead("design"); return; } setFocusCat(cat || null); setPersonalise(true); };
  const studio = builder.design_studio_mode;
  const isScan = house.kind === "scan";
  const skin = pv || resolveSkin(builder, house);
  const rank = sectionRank(skin);
  const pinLogo = skin.logo_pins ? builder.icon_url || builder.logo_url : "";
  const barCls = ["dark", "glass"].includes(skin.bar_style) ? "glass-dark !border-x-0 !border-t-0" : barClass(skin.bar_style);
  const strip = skin.menu_layout === "bottom" && !presenter && !edit;

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-slate-950 text-white" {...skinRootProps(skin)} data-testid="viewer-page">
      {mp.src && <iframe ref={mp.iframeRef} src={mp.src} title="3D tour" className="absolute inset-0 w-full h-full border-0" allow="xr-spatial-tracking; fullscreen" allowFullScreen data-testid="matterport-iframe" />}
      {mp.src && !edit && <div className="absolute bottom-0 right-0 z-[5] w-32 h-28 bg-[radial-gradient(ellipse_at_bottom_right,#020617_55%,transparent_75%)]" data-testid="mp-cover-bottom-right" />}
      {mp.src && !edit && <div className="absolute bottom-0 left-0 z-[5] w-24 h-24 bg-[radial-gradient(ellipse_at_bottom_left,#020617_50%,transparent_72%)]" data-testid="mp-cover-bottom-left" />}
      {house.watermark && !edit && <Watermark builder={builder} />}
      {intro && house.start_screen !== false && !edit && !presenter && !skinPreview && <StartScreen builder={builder} house={house} agent={bundle.agent} onStart={() => setIntro(false)} />}
      <Fallback status={mp.status} house={house} />
      {mp.src && !skinPreview && <LoadingVeil show={veil} house={house} builder={builder} />}
      <HotspotLayer sdk={mp.sdk} iframeRef={mp.iframeRef} sweepsRef={mp.sweepsRef} logo={pinLogo} pin={{ size: skin.pin_size, shape: skin.pin_shape, color: skin.pin_color, labels: skin.pin_labels }} hotspots={hotspots} choices={choices} finishes={finishes} onOpen={(h) => select(h, true)} />

      {floor && (!mp.sdk || !edit) && (
        <div className="absolute inset-x-0 bottom-0 top-16 z-20 bg-slate-950/95 grid place-items-center p-3 sm:p-6" data-testid="floorplan-overlay">
          {house.iguide_url && (planTab === "iguide" || !(house.floorplan_url || (house.floorplans || []).length)) ? <IguidePlans url={house.iguide_url} /> : <FloorPlanViewer house={house} hotspots={hotspots} logo={pinLogo} onPick={(h) => { setFloor(false); select(h); }} />}
          {house.iguide_url && (house.floorplan_url || (house.floorplans || []).length > 0) && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 glass-dark rounded-full p-1 flex gap-1" data-testid="floorplan-tabs">
              {[["plan", "Floor plan"], ["iguide", "Measured plans"]].map(([k, l]) => <button key={k} onClick={() => setPlanTab(k)} className={`h-8 px-4 rounded-full text-xs font-semibold ${planTab === k ? "bg-white text-slate-900" : "hover:bg-white/10"}`} data-testid={`floorplan-tab-${k}`}>{l}</button>)}
            </div>
          )}
          <button onClick={() => setFloor(false)} className="absolute top-20 right-4 w-11 h-11 rounded-full glass-dark grid place-items-center" data-testid="floorplan-close"><X className="w-5 h-5" /></button>
        </div>
      )}
      {gallery && <GalleryOverlay items={house.gallery || []} onClose={() => setGallery(false)} onBook={presenter ? null : () => { setGallery(false); book(); }} />}

      {tryMode && !intro && <a href="/#demo" className={`absolute z-40 top-20 left-1/2 -translate-x-1/2 transition-opacity duration-700 ${hideCls} rounded-full bg-[#D4A12A] text-slate-900 text-xs sm:text-sm font-semibold px-4 py-2 shadow-xl whitespace-nowrap`} data-testid="try-banner">{bundle.try?.listing ? "Your tour, your listing" : "Your tour, sample details"} · Book a demo to make it yours</a>}
      <header className={`absolute top-0 inset-x-0 z-30 h-16 transition-opacity duration-700 ${hideCls} ${barCls} backdrop-blur-md border-b border-white/10 shadow-lg px-2 sm:px-4 flex items-center gap-1.5 sm:gap-2`} data-testid="viewer-topbar">
        <div className="flex items-center gap-3 min-w-0 shrink">
          {!presenter && !embed && (canEdit
            ? <Link to={qs.get("from") === "setup" ? `/admin/homes/${house.id}` : "/admin/house-types"} className="h-8 pl-2 pr-3 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center gap-1.5 text-xs font-semibold shrink-0" data-testid="viewer-back-to-admin"><ArrowLeft className="w-4 h-4" /><span className="hidden sm:inline">Back to admin</span></Link>
            : <Link to={development ? `/d/${development.id}` : `/b/${builder.slug}`} className="w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center shrink-0" data-testid="viewer-back-link"><ArrowLeft className="w-4 h-4" /></Link>)}
          <span className="hidden sm:block"><BrandMark builder={builder} light={skin.bar_style !== "light"} /></span>
          <span className="hidden xl:block w-px h-6 bg-white/15" />
          <span className="truncate text-xs sm:text-sm max-w-[120px] xl:max-w-[200px] 2xl:max-w-[420px]" data-testid="viewer-house-name">{house.name}{plot ? ` · Lot ${plot.plot_number}` : ""}{house.asset && assetLine(house.asset) && <span className="hidden md:inline text-white/60"> · {assetLine(house.asset)}</span>}</span>
        </div>
        {!edit ? (
          <TourMenus hotspots={hotspots} onSelect={select} presenter={presenter} rank={rank}
            hasStops={(house.tour_stops || []).length > 0} onPlayTour={() => setPlaySignal(Date.now())}
            hasPlan={!!house.floorplan_url || !!house.iguide_url || (house.floorplans || []).length > 0} onFloor={() => setFloor(true)}
            hasGallery={(house.gallery || []).length > 0} onGallery={() => setGallery(true)}
            onBook={book} onRegister={() => setLead("interest")} extra={links.slice(1)} onLink={goLink} />
        ) : <div className="flex-1" />}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-auto">
          {!edit && !presenter && <ExtraButtons Btn={IconBtn} builder={builder} hasSuites={hotspots.some((x) => x.kind === "room") && builder.industry === "fitness"} audio={!!house.ambient_audio} muted={muted} onMute={toggleMute} onOpen={setExtra} />}
          {canEdit && !embed && !presenter && (edit
            ? <IconBtn onClick={() => setEdit(false)} className="!bg-[#D4A12A] !text-slate-900" data-testid="viewer-exit-edit"><Eye className="w-4 h-4" /><span className="hidden sm:inline">{vt().preview}</span></IconBtn>
            : <IconBtn onClick={() => setEdit(true)} className="!bg-[#D4A12A] !text-slate-900" data-testid="viewer-enter-edit"><Wand2 className="w-4 h-4" /><span className="hidden 2xl:inline">{vt().editLabel}</span></IconBtn>)}
          <IconBtn big={presenter} onClick={() => setRoomsSheet(true)} className="lg:hidden" data-testid="viewer-rooms-button"><List className="w-4 h-4" /></IconBtn>
          {house.has_pumpjack && (canEdit || user?.builder_id === builder.id || house.locked) && <IconBtn big={presenter} onClick={() => setRecords(true)} title="Pumpjack records" data-testid="viewer-records-button"><ClipboardList className="w-4 h-4" /><span className="hidden sm:inline">Records</span></IconBtn>}
          <IconBtn big={presenter} onClick={toggleUnit} title="Switch room sizes between feet and metres" data-testid="viewer-units-toggle"><Ruler className="w-4 h-4" /><span data-testid="viewer-units-label">{unit === "ft" ? "ft" : "m"}</span></IconBtn>
          {edit && house.gallery?.length > 0 && <IconBtn big={presenter} onClick={() => setGallery(true)} data-testid="viewer-gallery-button"><Images className="w-4 h-4" /></IconBtn>}
          <IconBtn big={presenter} active={floor} onClick={toggleFloor} className={edit ? "" : "lg:hidden"} data-testid="viewer-floorplan-button"><Map className="w-4 h-4" /></IconBtn>
          {isScan || !sells || skin.show_price === false ? null : studio ? (!presenter && <IconBtn onClick={() => setLead("design")} data-testid="viewer-design-studio-button"><Palette className="w-4 h-4" /><span className="hidden sm:inline">Design studio</span></IconBtn>) : (
            <IconBtn big={presenter} onClick={() => openPersonalise()} data-testid="viewer-personalise-button"><Palette className="w-4 h-4" /><span className="hidden sm:inline" data-testid="viewer-running-total">{money(total)}</span></IconBtn>
          )}
          {!presenter && <IconBtn onClick={book} className={links[0] ? "hidden md:inline-flex !bg-ba !text-white" : "hidden 2xl:inline-flex"} data-testid="viewer-book-viewing"><CalendarDays className="w-4 h-4" />{vt().book}</IconBtn>}
          <IconBtn big={presenter} active={presenter} onClick={togglePresenter} data-testid="viewer-presenter-toggle">{presenter ? <><Minimize2 className="w-4 h-4" />Exit</> : <MonitorPlay className="w-4 h-4" />}</IconBtn>
        </div>
      </header>
      {spaces.length > 1 && (
        <nav className={`absolute top-20 left-1/2 -translate-x-1/2 z-30 transition-opacity duration-700 ${hideCls} glass-dark rounded-full p-1 flex gap-1 max-w-[95vw] overflow-x-auto no-scrollbar`} data-testid="space-switcher">
          {spaces.map((s) => (
            <Link key={s.id} to={`/${embed ? "embed" : "view"}/${s.id}?spaces=${spaceIds.join(",")}`} className={`shrink-0 h-9 px-4 rounded-full text-sm inline-flex items-center ${s.id === id ? "bg-white text-slate-900" : "hover:bg-white/10"}`} data-testid={`space-switch-${s.id}`}>{s.name}</Link>
          ))}
        </nav>
      )}

      {!edit && skin.menu_layout === "left" && (
        <aside className={`hidden lg:flex flex-col absolute left-4 top-20 z-20 transition-opacity duration-700 ${hideCls} ${presenter ? "w-80" : "w-72"} max-h-[calc(100vh-11rem)] overflow-hidden glass-dark rounded-3xl p-3`} data-testid="viewer-room-sidebar">
          <div className="min-h-0 overflow-y-auto no-scrollbar overscroll-contain pb-6 [mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)]">
          <RoomList hotspots={hotspots} onSelect={select} activeId={open?.id} big={presenter} booked={booked} rank={rank} />
          </div>
        </aside>
      )}
      {edit && <div className="absolute left-4 top-20 z-20"><EditorPanel sdk={mp.sdk} iframeRef={mp.iframeRef} sweepsRef={mp.sweepsRef} poseRef={mp.poseRef} hitRef={mp.hitRef} bundle={bundle} reload={load} initialPinId={qs.get("pin") || ""} initialTab={qs.get("tab") === "tour" ? "tour" : "hotspots"} /></div>}

      {open && (
        <div className="absolute z-30 inset-x-3 bottom-24 sm:inset-x-auto sm:right-4 sm:top-20 sm:bottom-auto sm:w-96 text-slate-900">
          <HotspotDetail h={open} choices={choices} finishes={finishes} presenter={presenter} onClose={() => setOpen(null)} onPersonalise={openPersonalise} onBook={() => bookFrom(open)} booked={booked[open.id]} inside={hotspots.filter((x) => x.parent_id === open.id)} parent={hotspots.find((x) => x.id === open.parent_id) || null} onSelect={select} onShare={edit ? null : () => share(open)} />
        </div>
      )}

      <div className={`absolute z-20 inset-x-0 transition-opacity duration-700 ${hideCls} ${presenter || strip ? "bottom-24" : "bottom-4"} flex justify-center px-3 pointer-events-none`}>
        <div className="pointer-events-auto"><TourBar sdk={mp.sdk} stops={house.tour_stops || []} transition={house.tour_transition} speed={skin.move_speed} narrate={house.narrate_captions !== false} playSignal={playSignal} big={presenter} onPlay={() => track({ type: "tour_play", house_type_id: house.id })} /></div>
      </div>

      {strip && (
        <nav className={`absolute z-20 bottom-0 inset-x-0 p-2 sm:p-3 glass-dark !rounded-none flex gap-2 overflow-x-auto no-scrollbar transition-opacity duration-700 ${hideCls}`} data-testid="viewer-room-strip">
          {hotspots.filter((h) => h.kind === "room" || h.kind === "part" || h.kind === "area").map((h) => (
            <button key={h.id} onClick={() => select(h)} className={`shrink-0 h-12 px-4 rounded-2xl text-sm font-semibold transition-colors ${open?.id === h.id ? "bg-white text-slate-900" : "bg-white/10 hover:bg-white/20"}`} data-testid={`room-strip-${h.id}`}>{h.title}</button>
          ))}
        </nav>
      )}

      {presenter && (
        <nav className="absolute z-20 bottom-0 inset-x-0 p-3 glass-dark !rounded-none flex gap-2 overflow-x-auto no-scrollbar" data-testid="presenter-room-bar">
          {hotspots.filter((h) => h.kind === "room" || h.kind === "part" || h.kind === "area").map((h) => (
            <button key={h.id} onClick={() => select(h)} className={`shrink-0 min-h-[64px] px-6 rounded-2xl text-base font-semibold ${open?.id === h.id ? "bg-white text-slate-900" : "bg-white/10"}`} data-testid={`presenter-room-${h.id}`}>
              {h.title}{h.dimensions && <span className="block text-xs font-normal opacity-70">{fmtDims(h.dimensions, unit)}</span>}
            </button>
          ))}
        </nav>
      )}

      {!presenter && !edit && !strip && <div className={`absolute z-30 left-3 bottom-4 lg:hidden text-white transition-opacity duration-700 ${hideCls} ${open ? "right-3 sm:right-auto sm:w-60" : "w-[min(15rem,calc(100%-7.5rem))] sm:w-[min(15rem,calc(100%-14.5rem))] min-w-[9.5rem]"}`}><RoomSwiper rooms={hotspots.filter((h) => h.kind === "room" || h.kind === "part" || h.kind === "area")} activeId={open?.id} onSelect={select} /></div>}
      {!presenter && !edit && <div className={`absolute z-30 right-3 transition-opacity duration-700 ${hideCls} ${open ? "hidden sm:block" : ""} ${strip ? "bottom-24" : "bottom-4"}`}><ChatWidget houseTypeId={house.id} plotId={plot?.id} builderName={builder.name} onBook={book} skin={skin} suggestions={builder.chat_suggestions || []} welcome={house.chat_welcome} /></div>}
      <CtaChooser />
      <GlideCaption cap={glideCap} />
      {tryMode && <TryTip cap={glideCap} />}
      {tryMode && <TryTitle show={titleShow} name={bundle.try?.name || "you"} />}
      {sells && !isScan && <ClosingCard show={closing} house={house} builder={builder} unit={unit} onBook={book} bookLabel={vt().book} agent={bundle.agent} />}
      {bundle.agent && !presenter && !edit && !intro && <AgentBadge agent={bundle.agent} className={`absolute z-30 right-3 transition-opacity duration-700 ${hideCls} ${strip ? "bottom-40" : "bottom-20"}`} />}
      {cine && ht?.ambient_audio && <button onPointerDown={(e) => e.stopPropagation()} onClick={toggleMute} title={muted ? "Play music" : "Mute music"} className="absolute z-40 top-4 right-4 w-11 h-11 rounded-full glass-dark text-white grid place-items-center" data-testid="cinematic-mute">{muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</button>}
      {needVideo && tourOn && <IntroVideo src={ht.intro_video} withSound={ht.start_screen !== false} onDone={() => setVideoDone(true)} />}
      {extra && <ExtraDialog which={extra} close={() => setExtra(null)} builder={builder} house={house} hotspots={hotspots} onSelect={select} onBook={(h) => { setExtra(null); bookFrom(h); }} />}

      <Sheet open={records} onOpenChange={setRecords}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="records-sheet">
          <SheetHeader><SheetTitle className="font-serif">Pumpjack records</SheetTitle></SheetHeader>
          {records && <PumpjackRecords h={house} url={`/public/house-types/${house.id}/pumpjack`} locked />}
        </SheetContent>
      </Sheet>
      <Sheet open={roomsSheet} onOpenChange={setRoomsSheet}>
        <SheetContent side="bottom" className="bg-slate-950 text-white border-slate-800 max-h-[75vh] overflow-y-auto no-scrollbar rounded-t-3xl" data-testid="rooms-sheet">
          <SheetHeader><SheetTitle className="text-white font-serif">{vt().sheet}</SheetTitle></SheetHeader>
          <CompactActions presenter={presenter} close={() => setRoomsSheet(false)}
            hasStops={(house.tour_stops || []).length > 0} onPlayTour={() => setPlaySignal(Date.now())}
            hasPlan={!!house.floorplan_url || !!house.iguide_url || (house.floorplans || []).length > 0} onFloor={() => setFloor(true)}
            hasGallery={(house.gallery || []).length > 0} onGallery={() => setGallery(true)}
            onBook={book} onRegister={() => setLead("interest")} extra={links.slice(1)} onLink={goLink} />
          <div className="mt-3"><RoomList hotspots={hotspots} onSelect={select} activeId={open?.id} big booked={booked} rank={rank} /></div>
        </SheetContent>
      </Sheet>
      {!studio && !isScan && sells && <PersonaliseDrawer builder={builder} open={personalise} onOpenChange={setPersonalise} finishes={finishes} choices={choices} onPick={pick} base={base} focusCat={focusCat} onSave={() => { setPersonalise(false); setSaving(true); }} />}
      <SaveHomeDialog open={saving} onOpenChange={setSaving} houseTypeId={house.id} plotId={plot?.id} choices={choices} />
      {!presenter && <LeadDialog open={!!lead} onOpenChange={(o) => !o && setLead(null)} kind={lead || "viewing"} builderId={builder.id} context={ctx} title={lead === "design" ? builder.design_studio_label : undefined} />}
    </div>
  );
}
